import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import createAxiosInstance from '../../services/AxiosInstance';
import {clearUserProfile, setUserProfile} from './userSlice';
import {User} from '../../model/User';

const HTTP_OK_STATUS = 200;

export interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    token: string | null;
}

export const initializeSession = createAsyncThunk<
    User,
    void,
    { rejectValue: string }
>('auth/initializeSession', async (_, thunkApi) => {
    const token = localStorage.getItem('jwt');

    if (!token) {
        thunkApi.dispatch(clearUserProfile());
        return thunkApi.rejectWithValue('missing-token');
    }

    try {
        const axios = createAxiosInstance();
        const authResponse = await axios.get('/auth/auth');

        if (authResponse.status !== HTTP_OK_STATUS) {
            thunkApi.dispatch(clearUserProfile());
            return thunkApi.rejectWithValue('unauthorized');
        }

        const userResponse = await axios.get('/user/personal-data/header');
        const user = userResponse.data as User;
        thunkApi.dispatch(setUserProfile(user));
        return user;
    } catch {
        thunkApi.dispatch(clearUserProfile());
        return thunkApi.rejectWithValue('unauthorized');
    }
});

const initialState: AuthState = {
    isAuthenticated: false,
    isLoading: true,
    token: localStorage.getItem('jwt'),
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setAuthLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        setAuthenticated(state, action: PayloadAction<boolean>) {
            state.isAuthenticated = action.payload;
        },
        setToken(state, action: PayloadAction<string | null>) {
            state.token = action.payload;
            state.isAuthenticated = Boolean(action.payload);
            if (action.payload) {
                localStorage.setItem('jwt', action.payload);
                return;
            }
            localStorage.removeItem('jwt');
        },
        clearAuth(state) {
            state.token = null;
            state.isAuthenticated = false;
            state.isLoading = false;
            localStorage.removeItem('jwt');
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initializeSession.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(initializeSession.fulfilled, (state) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.token = localStorage.getItem('jwt');
            })
            .addCase(initializeSession.rejected, (state) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.token = null;
                localStorage.removeItem('jwt');
            });
    },
});

export const {setAuthLoading, setAuthenticated, setToken, clearAuth} = authSlice.actions;
export default authSlice.reducer;
